import { useState, useCallback, memo, useRef, useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { ModalCloseButton } from '../ModalCloseButton';
import { MetricHistory } from './MetricHistory';
import type { MetricEntryResponse } from '../../types/api';
import './MetricPillInput.css';

interface MetricPillInputProps {
  label: string;
  instruction?: string;
  value: number | null;
  commonValues: readonly number[];
  unit?: string;
  onChange: (value: number | null) => void;
  min?: number;
  max?: number;
  decimalPlaces?: number;
  error?: string;
  /**
   * Reject a custom entry outside [min, max] with a visible message, instead of silently
   * clamping it to the bound. Off by default so every existing metric keeps the behaviour it
   * has always had — opt in where the range is a real constraint a coach should be told about
   * rather than a typo guard (spin rate). Silently rewriting 4500 to 2800 puts a number the
   * coach never measured on a recruiting record.
   */
  rejectOutOfRange?: boolean;
  /** Optional: Enable history tracking by providing prospectId */
  prospectId?: string;
  /** Optional: The metric field name for history queries (e.g., 'exitVelocity') */
  metricName?: string;
  /** Optional: The metric category ('position_player' or 'pitcher') */
  metricCategory?: 'position_player' | 'pitcher';
  /** Optional: All metric entries for the prospect (from prospect response) */
  metricEntries?: MetricEntryResponse[];
  /** Optional: Current user ID to show "You" for entries recorded by the logged-in user */
  currentUserId?: string;
}

interface PillButtonProps {
  value: number;
  onPress: () => void;
}

const PillButton = memo(function PillButton({ value, onPress }: PillButtonProps) {
  return (
    <button
      type="button"
      className="metric-pill-input__pill"
      onClick={onPress}
    >
      {value}
    </button>
  );
});

function MetricPillInputComponent({
  label,
  instruction,
  commonValues,
  unit,
  onChange,
  min,
  max,
  decimalPlaces = 1,
  error,
  rejectOutOfRange = false,
  prospectId,
  metricName,
  metricCategory,
  metricEntries = [],
  currentUserId,
}: MetricPillInputProps) {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [customValue, setCustomValue] = useState<string>('');
  const [rejection, setRejection] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const labelId = useId();
  const rejectionId = useId();
  const modalLabelId = useId();

  // Determine if history features should be enabled
  const historyEnabled = Boolean(prospectId && metricName && metricCategory);

  const handlePillPress = useCallback((pillValue: number) => {
    onChange(pillValue);
  }, [onChange]);

  // Focus input without scrolling when modal opens
  useEffect(() => {
    if (isModalVisible && inputRef.current) {
      inputRef.current.focus({ preventScroll: true });
    }
  }, [isModalVisible]);

  const handleCustomPress = useCallback(() => {
    setCustomValue('');
    setRejection(null);
    setIsModalVisible(true);
  }, []);

  const handleAdd = useCallback(() => {
    if (customValue === '') {
      onChange(null);
      setIsModalVisible(false);
      return;
    }

    const parsed = parseFloat(customValue);
    if (isNaN(parsed)) {
      setIsModalVisible(false);
      return;
    }

    const lower = min ?? 0;
    const upper = max ?? 999;

    // Rejecting keeps the modal open with the reason, so the coach corrects the reading
    // rather than discovering later that a value they never measured was saved.
    if (rejectOutOfRange && (parsed < lower || parsed > upper)) {
      setRejection(`${label} must be between ${lower} and ${upper}`);
      return;
    }

    const clamped = Math.max(lower, Math.min(upper, parsed));
    const multiplier = Math.pow(10, decimalPlaces);
    onChange(Math.round(clamped * multiplier) / multiplier);
    setIsModalVisible(false);
  }, [customValue, onChange, min, max, decimalPlaces, rejectOutOfRange, label]);

  const handleClose = useCallback(() => {
    setIsModalVisible(false);
  }, []);

  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  }, [handleClose]);

  const modalContent = isModalVisible && (
    <div className="metric-pill-input__modal-overlay" onClick={handleOverlayClick}>
      <div className="metric-pill-input__modal-card" onClick={(e) => e.stopPropagation()}>
        <ModalCloseButton className="metric-pill-input__close-button" onClick={handleClose} />

        <p id={modalLabelId} className="metric-pill-input__modal-label">
          {label} {unit && `(${unit})`}
        </p>

        <input
          ref={inputRef}
          type="number"
          aria-labelledby={modalLabelId}
          className="metric-pill-input__large-input"
          value={customValue}
          onChange={(e) => {
            setCustomValue(e.target.value);
            setRejection(null);
          }}
          placeholder="0"
          step={decimalPlaces === 0 ? '1' : `0.${'0'.repeat(decimalPlaces - 1)}1`}
          aria-invalid={rejection ? true : undefined}
          aria-describedby={rejection ? rejectionId : undefined}
        />

        {rejection && (
          <p id={rejectionId} role="alert" className="metric-pill-input__modal-error">
            {rejection}
          </p>
        )}

        <div className="metric-pill-input__button-container">
          <button
            type="button"
            className="metric-pill-input__add-button"
            onClick={handleAdd}
          >
            Add
          </button>
        </div>
      </div>
    </div>
  );

  const content = (
    <div className="metric-pill-input__container">
      {/*
        A <span>, not a <label>: this names a SET of buttons rather than one control, so there is
        no htmlFor to give it. See the group-label rule in the root CLAUDE.md.
      */}
      <span id={labelId} className="metric-pill-input__label">{label}</span>
      {instruction && <p className="metric-pill-input__instruction">{instruction}</p>}

      {/*
        The pills are a set of buttons, not one control, so the label above cannot be wired to
        them with htmlFor. Naming the group is what stops a screen reader announcing a bare
        "1700" or one of the many identically-named "Custom" buttons with no idea which metric
        it belongs to — a softball pitch now shows two of these groups side by side.
      */}
      <div
        className="metric-pill-input__pills"
        role="group"
        aria-labelledby={labelId}
        data-testid={metricName ? `metric-pills-${metricName}` : undefined}
      >
        {commonValues.map((pillValue) => (
          <PillButton
            key={pillValue}
            value={pillValue}
            onPress={() => handlePillPress(pillValue)}
          />
        ))}
        <button
          type="button"
          className="metric-pill-input__custom-pill"
          onClick={handleCustomPress}
        >
          Custom
        </button>
      </div>

      {error && <p className="metric-pill-input__error">{error}</p>}

      {historyEnabled && (
        <MetricHistory
          allEntries={metricEntries}
          metricName={metricName!}
          unit={unit}
          currentUserId={currentUserId}
        />
      )}
    </div>
  );

  return (
    <>
      {content}
      {modalContent && createPortal(modalContent, document.body)}
    </>
  );
}

export const MetricPillInput = memo(MetricPillInputComponent);
